import { apiRequest } from '../../../shared/services/apiClient';

export interface EmailComposeLayout {
  id: string;
  name: string;
  isDefault: boolean;
}

export interface EmailComposeSnippet {
  id: string;
  name: string;
  shortcut: string | null;
  category: string | null;
  bodyHtml: string;
}

export interface EmailComposeOptions {
  canSend: boolean;
  recipient: string | null;
  defaultSubject: string;
  replyFormat: 'html' | 'text';
  useBrandLayout: boolean;
  brandingEnabled: boolean;
  mailboxLayoutId: string | null;
  hasSignature: boolean;
  companyName: string | null;
  layouts: EmailComposeLayout[];
  snippets: EmailComposeSnippet[];
  /** The mailbox lets agents' personal signatures replace its own. */
  agentSignaturesEnabled: boolean;
  /** The composing agent's signature as recipients see it; null when they have none. */
  agentSignatureHtml: string | null;
}

export interface EmailAgentSignature {
  bodyHtml: string;
  previewHtml: string;
  updatedAt: string | null;
  variables: string[];
}

export interface EmailComposePreview {
  html: string;
  text: string;
  htmlBytes: number;
  exceedsClipLimit: boolean;
}

export interface EmailMessageRendering {
  messageId: string;
  subject: string;
  recipient: string;
  html: string | null;
  text: string | null;
  layoutName: string | null;
  layoutVersion: number | null;
  sentAt: string;
}

/** Sent with a conversation message so the email relay uses the rich body, subject and layout. */
export interface ConversationEmailOptions {
  subject?: string;
  htmlBody?: string;
  layoutId?: string;
  snippetIds?: string[];
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const unwrap = (payload: unknown): Record<string, unknown> => {
  const record = asRecord(payload);
  return record.data && typeof record.data === 'object' ? asRecord(record.data) : record;
};

const optionalString = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value : null);

const base = (conversationId: string) => `/api/v1/conversations/${conversationId}/email`;

export const normalizeEmailComposeOptions = (payload: unknown): EmailComposeOptions => {
  const data = unwrap(payload);
  const layouts = Array.isArray(data.layouts) ? data.layouts : [];
  const snippets = Array.isArray(data.snippets) ? data.snippets : [];
  return {
    canSend: Boolean(data.canSend),
    recipient: optionalString(data.recipient),
    defaultSubject: String(data.defaultSubject ?? ''),
    replyFormat: data.replyFormat === 'text' ? 'text' : 'html',
    useBrandLayout: data.useBrandLayout !== false,
    brandingEnabled: Boolean(data.brandingEnabled),
    mailboxLayoutId: optionalString(data.mailboxLayoutId),
    hasSignature: Boolean(data.hasSignature),
    companyName: optionalString(data.companyName),
    layouts: layouts.map(asRecord).map(item => ({
      id: String(item.id ?? ''),
      name: String(item.name ?? ''),
      isDefault: Boolean(item.isDefault),
    })),
    snippets: snippets.map(asRecord).map(item => ({
      id: String(item.id ?? ''),
      name: String(item.name ?? ''),
      shortcut: optionalString(item.shortcut),
      category: optionalString(item.category),
      bodyHtml: String(item.bodyHtml ?? ''),
    })),
    agentSignaturesEnabled: data.agentSignaturesEnabled !== false,
    agentSignatureHtml: optionalString(data.agentSignatureHtml),
  };
};

const SIGNATURE_BASE = '/api/v1/email/signatures/me';

const normalizeSignature = (payload: unknown): EmailAgentSignature => {
  const data = unwrap(payload);
  return {
    bodyHtml: String(data.bodyHtml ?? ''),
    previewHtml: String(data.previewHtml ?? ''),
    updatedAt: optionalString(data.updatedAt),
    variables: (Array.isArray(data.variables) ? data.variables : []).map(String),
  };
};

export const getMyEmailSignature = async (): Promise<EmailAgentSignature> =>
  normalizeSignature(await apiRequest<unknown>(SIGNATURE_BASE));

/** Saving an empty body removes the signature. */
export const saveMyEmailSignature = async (bodyHtml: string): Promise<EmailAgentSignature> =>
  normalizeSignature(await apiRequest<unknown>(SIGNATURE_BASE, { method: 'PUT', body: JSON.stringify({ bodyHtml }) }));

export const getEmailComposeOptions = async (conversationId: string): Promise<EmailComposeOptions> =>
  normalizeEmailComposeOptions(await apiRequest<unknown>(`${base(conversationId)}/compose-options`));

export const previewEmailCompose = async (
  conversationId: string,
  request: { subject: string; htmlBody: string; text: string; layoutId?: string },
): Promise<EmailComposePreview> => {
  const data = unwrap(await apiRequest<unknown>(`${base(conversationId)}/preview`, {
    method: 'POST',
    body: JSON.stringify({ ...request, layoutId: request.layoutId || undefined }),
  }));
  return {
    html: String(data.html ?? ''),
    text: String(data.text ?? ''),
    htmlBytes: Number(data.htmlBytes ?? 0),
    exceedsClipLimit: Boolean(data.exceedsClipLimit),
  };
};

export const getEmailMessageRendering = async (conversationId: string, messageId: string): Promise<EmailMessageRendering> => {
  const data = unwrap(await apiRequest<unknown>(`${base(conversationId)}/messages/${messageId}`));
  return {
    messageId: String(data.messageId ?? messageId),
    subject: String(data.subject ?? ''),
    recipient: String(data.recipient ?? ''),
    html: optionalString(data.html),
    text: optionalString(data.text),
    layoutName: optionalString(data.layoutName),
    layoutVersion: typeof data.layoutVersion === 'number' ? data.layoutVersion : null,
    sentAt: String(data.sentAt ?? ''),
  };
};

export interface SnippetVariables {
  contactName?: string | null;
  agentName?: string | null;
  companyName?: string | null;
}

const firstName = (name?: string | null) => name?.trim().split(/\s+/)[0] ?? '';

const escapeHtml = (value: string) => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

/** Unknown or empty variables are dropped rather than left as raw `{{...}}` in a customer email. */
export const fillSnippetVariables = (html: string, variables: SnippetVariables): string => {
  const values: Record<string, string> = {
    'contact.name': variables.contactName?.trim() ?? '',
    'contact.firstname': firstName(variables.contactName),
    'agent.name': variables.agentName?.trim() ?? '',
    'agent.firstname': firstName(variables.agentName),
    'company.name': variables.companyName?.trim() ?? '',
  };
  return html.replace(/\{\{\s*([a-zA-Z.]+)\s*\}\}/g, (_, key: string) => escapeHtml(values[key.toLowerCase()] ?? ''));
};

/** Plain-text part of a TipTap document: blank lines between paragraphs, one line per list item. */
export const htmlToPlainText = (html: string): string => {
  if (!html.trim()) {
    return '';
  }
  const container = document.createElement('div');
  container.innerHTML = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<li([^>]*)>\s*<p[^>]*>/gi, '<li$1>')
    .replace(/<\/p>\s*<\/li>/gi, '</li>')
    .replace(/<li[^>]*>/gi, '<li>• ')
    .replace(/<\/li>/gi, '</li>\n')
    .replace(/<\/(p|h[1-6]|blockquote|pre|ul|ol)>/gi, '</$1>\n\n');
  return (container.textContent ?? '')
    .split('\n')
    .map(line => line.replace(/\s+$/g, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/** Turns plain composer text into paragraphs so a draft carries over into the rich editor. */
export const plainTextToHtml = (text: string): string => text
  .trim()
  .split(/\n{2,}/)
  .filter(Boolean)
  .map(paragraph => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
  .join('');
